import {
  ALLOWED_PLATFORM_IDS,
  COUPON_CATEGORIES,
  COUPON_CATEGORY,
  COUPON_VALIDATION,
  DISCOUNT_TYPES,
  ZERO,
} from "constants/coupons";
import { normalizeDiscountType } from "helpers/couponDiscountType";
import {
  isFlatDiscount,
  isPercentageDiscount,
} from "helpers/couponDiscountType";
import { normalizeTenantIdList } from "helpers/couponScopeOptions";
import { collectStrictIntegerIdList, parsePackageIds } from "helpers/packageIds";
import { normalizeDate } from "utils/dateUtils";

const COUPON_CODE_REGEX = new RegExp(COUPON_VALIDATION.COUPON_CODE_REGEX);

const hasMaxDecimalPlaces = (value, maxPlaces) => {
  if (value === "" || value === null || value === undefined) return true;
  const stringValue = String(value).trim();
  const decimalIndex = stringValue.indexOf(".");
  if (decimalIndex === -1) return true;
  return stringValue.length - decimalIndex - 1 <= maxPlaces;
};

const decimalPlacesError = (label) =>
  `${label} can have at most ${COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES} decimal places.`;

const validateAmountDecimalPlaces = (value, fieldName, label, errors) => {
  if (value === "" || value === null || value === undefined) return;
  if (
    !hasMaxDecimalPlaces(value, COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES)
  ) {
    errors[fieldName] = decimalPlacesError(label);
  }
};

const validateDiscountTypeField = (values, errors) => {
  const normalizedDiscountType = normalizeDiscountType(values.discountType);

  if (normalizedDiscountType === "") {
    errors.discountType = "Discount type is required.";
    return;
  }

  if (
    normalizedDiscountType !== DISCOUNT_TYPES.FLAT &&
    normalizedDiscountType !== DISCOUNT_TYPES.PERCENTAGE
  ) {
    errors.discountType = "Discount type must be FLAT or PERCENTAGE.";
  }
};

const validateFlatMaxAllowedAmountMustBeEmpty = (values, errors) => {
  if (normalizeDiscountType(values.discountType) !== DISCOUNT_TYPES.FLAT) {
    return;
  }

  const maxAllowedAmount = values.maxAllowedAmount;
  if (
    maxAllowedAmount !== "" &&
    maxAllowedAmount !== null &&
    maxAllowedAmount !== undefined
  ) {
    errors.maxAllowedAmount =
      "Max allowed amount must not be provided for flat discounts.";
  }
};

const validateApplicableMultipleTimesField = (values, errors) => {
  if (
    values.isApplicableMultipleTimes === null ||
    values.isApplicableMultipleTimes === undefined
  ) {
    errors.isApplicableMultipleTimes =
      "Please specify whether the coupon can be applied multiple times.";
  }
};

const validateApplicableForSingleUserField = (values, errors) => {
  if (
    values.isApplicableForSingleUser === null ||
    values.isApplicableForSingleUser === undefined
  ) {
    errors.isApplicableForSingleUser =
      "Please specify whether the coupon is for a single user.";
  }
};

const validateUamIdField = (values, errors) => {
  if (values.isApplicableForSingleUser === true) {
    const uamId = Number(values.uamId);
    if (
      values.uamId === "" ||
      values.uamId === null ||
      values.uamId === undefined ||
      Number.isNaN(uamId)
    ) {
      errors.uamId = "UAM ID is required when the coupon is for a single user.";
      return;
    }

    if (!Number.isInteger(uamId)) {
      errors.uamId = "UAM ID must be a whole number.";
    }
    return;
  }

  if (
    values.uamId !== "" &&
    values.uamId !== null &&
    values.uamId !== undefined
  ) {
    errors.uamId =
      "UAM ID must not be provided when the coupon is not restricted to a single user.";
  }
};

const BUNDLE_DISCOUNT_CATEGORY_MESSAGE =
  'Category must be "Bundle Discount" when minimum cart MRP is greater than 0.';

const BUNDLE_DISCOUNT_EDIT_CART_MESSAGE =
  "Minimum cart MRP above 0 requires Bundle Discount category, which cannot be changed while editing.";

const validateBundleDiscountCategoryField = (values, errors, { isEdit = false } = {}) => {
  const hasMinimumCartMrp = Object.values(values.currencySettings ?? {}).some(
    (settings) => {
      const minimumCartMrp = Number(settings.minimumCartMrp);
      return (
        settings.minimumCartMrp !== "" &&
        settings.minimumCartMrp !== null &&
        settings.minimumCartMrp !== undefined &&
        !Number.isNaN(minimumCartMrp) &&
        minimumCartMrp > COUPON_VALIDATION.MIN_MRP_AMOUNT
      );
    }
  );

  if (
    hasMinimumCartMrp &&
    values.category !== COUPON_CATEGORY.BUNDLE_DISCOUNT
  ) {
    if (isEdit) {
      errors.currencySettings = BUNDLE_DISCOUNT_EDIT_CART_MESSAGE;
    } else {
      errors.category = BUNDLE_DISCOUNT_CATEGORY_MESSAGE;
    }
  }
};

const validateCouponRemarks = (values, errors) => {
  const remarks = (values.remarks ?? "").trim();
  if (remarks === "") {
    errors.remarks = "Remarks are required.";
  } else if (remarks.length > COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH) {
    errors.remarks = `Remarks cannot exceed ${COUPON_VALIDATION.MAX_COUPON_REMARKS_LENGTH} characters.`;
  }
};

const validateCouponScopeCurrencyAndTenant = (
  values,
  errors,
  {
    availableTenantOptionIds = [],
    selectedCurrencyIds = [],
    areTenantsConfirmed = true,
    isCreate = false,
  } = {}
) => {
  const selectedTenantIds = normalizeTenantIdList(values.tenantIds);

  if (selectedTenantIds.length === ZERO) {
    errors.tenantIds = "At least one tenant is required.";
    return;
  }

  if (isCreate && !areTenantsConfirmed) {
    errors.tenantIds =
      "Click Done to confirm tenants before continuing.";
    return;
  }

  if (selectedCurrencyIds.length === ZERO) {
    errors.tenantIds =
      "Selected tenants must include at least one currency.";
    return;
  }

  if (availableTenantOptionIds.length === ZERO) {
    return;
  }

  const allowedTenantOptionIds = new Set(
    availableTenantOptionIds.map((tenantOptionId) => String(tenantOptionId))
  );
  const invalidTenantIds = selectedTenantIds.filter(
    (tenantId) => !allowedTenantOptionIds.has(tenantId)
  );

  if (invalidTenantIds.length > ZERO) {
    errors.tenantIds = `Invalid tenant selection: ${invalidTenantIds.join(", ")}.`;
  }
};

const validateDiscountValueField = (values, errors) => {
  if (!isPercentageDiscount(values.discountType)) {
    return;
  }

  const discountValue = Number(values.discountValue);

  if (
    values.discountValue === "" ||
    values.discountValue === null ||
    values.discountValue === undefined ||
    Number.isNaN(discountValue)
  ) {
    errors.discountValue = "Discount percentage is required.";
    return;
  }

  if (
    !hasMaxDecimalPlaces(
      values.discountValue,
      COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES
    )
  ) {
    errors.discountValue = decimalPlacesError("Discount percentage");
    return;
  }

  if (
    discountValue <= COUPON_VALIDATION.MIN_PERCENTAGE_DISCOUNT ||
    discountValue > COUPON_VALIDATION.MAX_PERCENTAGE_DISCOUNT
  ) {
    errors.discountValue =
      "Percentage discount must be greater than 0 and at most 100.";
  }
};

const validateCurrencySettingsFields = (values, errors, options = {}) => {
  const currencySettings = values.currencySettings ?? {};
  const selectedCurrencyIds = options.selectedCurrencyIds ?? [];
  const currencyIds =
    selectedCurrencyIds.length > ZERO
      ? selectedCurrencyIds.map((currencyId) => String(currencyId))
      : Object.keys(currencySettings);
  const isPercentage = isPercentageDiscount(values.discountType);
  const isFlat = isFlatDiscount(values.discountType);

  if (!isPercentage && !isFlat) {
    return;
  }

  if (currencyIds.length === ZERO) {
    errors.currencySettings =
      "Select tenants to configure discount values for each currency.";
    return;
  }

  const currencyErrors = {};

  currencyIds.forEach((currencyId) => {
    const settings = currencySettings[currencyId] ?? {};
    const fieldErrors = {};

    if (isFlat) {
      const discountAmount = settings.discountAmount;
      const amount = Number(discountAmount);

      if (
        discountAmount === "" ||
        discountAmount === null ||
        discountAmount === undefined ||
        Number.isNaN(amount)
      ) {
        fieldErrors.discountAmount = "Flat discount amount is required.";
      } else if (
        !hasMaxDecimalPlaces(
          discountAmount,
          COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES
        )
      ) {
        fieldErrors.discountAmount = decimalPlacesError("Flat discount amount");
      } else if (amount <= COUPON_VALIDATION.MIN_FLAT_DISCOUNT_AMOUNT) {
        fieldErrors.discountAmount = "Flat discount amount must be greater than 0.";
      } else if (amount > COUPON_VALIDATION.MAX_FLAT_DISCOUNT_AMOUNT) {
        fieldErrors.discountAmount = `Flat discount must be less than or equal to ${COUPON_VALIDATION.MAX_FLAT_DISCOUNT_AMOUNT}.`;
      }
    }

    if (isPercentage) {
      const maxAllowedAmount = settings.maxAllowedAmount;
      const maxAmount = Number(maxAllowedAmount);

      if (
        maxAllowedAmount === "" ||
        maxAllowedAmount === null ||
        maxAllowedAmount === undefined ||
        Number.isNaN(maxAmount)
      ) {
        fieldErrors.maxAllowedAmount =
          "Max allowed amount is required for percentage discounts.";
      } else if (
        !hasMaxDecimalPlaces(
          maxAllowedAmount,
          COUPON_VALIDATION.MAX_DISCOUNT_DECIMAL_PLACES
        )
      ) {
        fieldErrors.maxAllowedAmount = decimalPlacesError("Max allowed amount");
      } else if (maxAmount <= COUPON_VALIDATION.MIN_MRP_AMOUNT) {
        fieldErrors.maxAllowedAmount =
          "Max allowed amount must be greater than 0.";
      }
    }

    const minimumPackageMrp = Number(settings.minimumPackageMrp);
    if (
      settings.minimumPackageMrp === "" ||
      settings.minimumPackageMrp === null ||
      settings.minimumPackageMrp === undefined ||
      Number.isNaN(minimumPackageMrp)
    ) {
      fieldErrors.minimumPackageMrp = "Minimum package MRP is required.";
    } else if (minimumPackageMrp < COUPON_VALIDATION.MIN_MRP_AMOUNT) {
      fieldErrors.minimumPackageMrp =
        "Minimum package MRP cannot be negative.";
    } else {
      validateAmountDecimalPlaces(
        settings.minimumPackageMrp,
        "minimumPackageMrp",
        "Minimum package MRP",
        fieldErrors
      );
    }

    if (
      settings.minimumCartMrp !== "" &&
      settings.minimumCartMrp !== null &&
      settings.minimumCartMrp !== undefined
    ) {
      const minimumCartMrp = Number(settings.minimumCartMrp);
      if (Number.isNaN(minimumCartMrp)) {
        fieldErrors.minimumCartMrp = "Minimum cart MRP must be a valid number.";
      } else if (minimumCartMrp < COUPON_VALIDATION.MIN_MRP_AMOUNT) {
        fieldErrors.minimumCartMrp = "Minimum cart MRP cannot be negative.";
      } else {
        validateAmountDecimalPlaces(
          settings.minimumCartMrp,
          "minimumCartMrp",
          "Minimum cart MRP",
          fieldErrors
        );
      }
    }

    if (Object.keys(fieldErrors).length > ZERO) {
      currencyErrors[currencyId] = fieldErrors;
    }
  });

  if (Object.keys(currencyErrors).length > ZERO) {
    errors.currencySettings = currencyErrors;
  }
};

export const validateTotalCouponCountField = (
  values,
  errors,
  { minimumUsedCount = ZERO } = {}
) => {
  const totalCouponCount = Number(values.totalCouponCount);

  if (
    values.totalCouponCount === "" ||
    values.totalCouponCount === null ||
    values.totalCouponCount === undefined ||
    Number.isNaN(totalCouponCount)
  ) {
    errors.totalCouponCount = "Total coupon count is required.";
    return;
  }

  if (
    !Number.isInteger(totalCouponCount) ||
    totalCouponCount < ZERO
  ) {
    errors.totalCouponCount = "Coupon count must be a whole number.";
    return;
  }

  if (totalCouponCount === ZERO) {
    errors.totalCouponCount = "Coupon count must be greater than 0.";
    return;
  }

  if (totalCouponCount > COUPON_VALIDATION.MAX_TOTAL_COUPON_COUNT) {
    errors.totalCouponCount =
      "Coupons to be created must be less than or equal to max allowed coupons count.";
    return;
  }

  const usedCountFloor = Math.max(minimumUsedCount, ZERO);
  if (usedCountFloor > ZERO && totalCouponCount < usedCountFloor) {
    errors.totalCouponCount = `Total cannot be less than ${usedCountFloor.toLocaleString()} coupons already used.`;
  }
};

const validatePackageApplicabilityFields = (
  values,
  errors,
  { availablePackageIds = [] } = {}
) => {
  if (
    values.selectAllPackages === null ||
    values.selectAllPackages === undefined
  ) {
    errors.selectAllPackages = "Please specify package applicability.";
    return;
  }

  if (values.selectAllPackages === true) {
    const packageIds = parsePackageIds(values.packageIds);
    if (packageIds.length > ZERO) {
      errors.packageIds =
        "Package IDs must be empty when all packages are selected.";
    }
    return;
  }

  const { ids: packageIds, invalidSegments } = collectStrictIntegerIdList(
    values.packageIds
  );

  if (invalidSegments.length > ZERO) {
    errors.packageIds = "Package IDs must be comma-separated whole numbers.";
    return;
  }

  if (packageIds.length === ZERO) {
    errors.packageIds =
      "Select at least one package when all packages is not selected.";
    return;
  }

  if (packageIds.includes(COUPON_VALIDATION.ALL_PACKAGES_ID)) {
    errors.packageIds = "Reserved all-packages ID (0) is not allowed.";
    return;
  }

  if (availablePackageIds.length > ZERO) {
    const unknownPackageIds = packageIds.filter(
      (packageId) => !availablePackageIds.includes(packageId)
    );

    if (unknownPackageIds.length > ZERO) {
      errors.packageIds = `Invalid package IDs: ${unknownPackageIds.join(", ")}.`;
    }
  }
};

const validatePlatformApplicabilityFields = (values, errors) => {
  if (
    values.selectAllPlatforms === null ||
    values.selectAllPlatforms === undefined
  ) {
    errors.selectAllPlatforms = "Please specify platform applicability.";
    return;
  }

  if (values.selectAllPlatforms === true) {
    const platformIds = parsePackageIds(values.platformIds);
    if (platformIds.length > ZERO) {
      errors.platformIds =
        "Platform IDs must be empty when all platforms are selected.";
    }
    return;
  }

  const { ids: platformIds, invalidSegments } = collectStrictIntegerIdList(
    values.platformIds
  );

  if (invalidSegments.length > ZERO) {
    errors.platformIds = "Platform IDs must be comma-separated whole numbers.";
    return;
  }

  if (platformIds.length === ZERO) {
    errors.platformIds =
      "Select at least one platform when all platforms is not selected.";
    return;
  }

  if (
    platformIds.some(
      (platformId) => !ALLOWED_PLATFORM_IDS.includes(platformId)
    )
  ) {
    errors.platformIds = "Only Web, Android, and iOS platforms are allowed.";
    return;
  }

  if (platformIds.includes(COUPON_VALIDATION.ALL_PLATFORMS_ID)) {
    errors.platformIds = "Reserved all-platforms ID (1) is not allowed.";
  }
};

export const validateExpiryDateAgainstEffectiveDate = (
  effectiveDateValue,
  expiryDateValue,
  errors
) => {
  const effectiveDate = normalizeDate(effectiveDateValue);
  const expiryDate = normalizeDate(expiryDateValue);

  if (expiryDate === null) {
    errors.expiryDate = "Expiry date is required.";
    return;
  }

  if (effectiveDate === null) {
    return;
  }

  const minExpiry = new Date(effectiveDate);
  minExpiry.setDate(
    minExpiry.getDate() + COUPON_VALIDATION.MIN_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS
  );
  const maxExpiry = new Date(effectiveDate);
  maxExpiry.setDate(
    maxExpiry.getDate() +
      COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS
  );

  if (expiryDate < minExpiry) {
    errors.expiryDate = `Expiry date must be at least ${COUPON_VALIDATION.MIN_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS} day after the effective date.`;
  } else if (expiryDate > maxExpiry) {
    errors.expiryDate = `Expiry date cannot be more than ${COUPON_VALIDATION.MAX_EXPIRY_DATE_AFTER_EFFECTIVE_DAYS} days after the effective date.`;
  }
};

export const validateCreateCouponForm = (values, options = {}) => {
  const {
    now = new Date(),
    availablePackageIds = [],
    availableTenantOptionIds = [],
    selectedCurrencyIds = [],
    areTenantsConfirmed = false,
  } = options;
  const errors = {};
  const today = new Date(now);
  today.setHours(ZERO, ZERO, ZERO, ZERO);

  const code = (values.code ?? "").trim();
  if (code === "") {
    errors.code = "Coupon code is required.";
  } else if (/\s/.test(code)) {
    errors.code = "Coupon code cannot contain whitespace.";
  } else if (code.length > COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH) {
    errors.code = `Coupon code cannot exceed ${COUPON_VALIDATION.MAX_COUPON_CODE_LENGTH} characters.`;
  } else if (!COUPON_CODE_REGEX.test(code)) {
    errors.code =
      "Coupon code must start with a letter or number and may contain only letters, digits, hyphen, and underscore.";
  }

  const category = (values.category ?? "").trim();
  if (category === "") {
    errors.category = "Coupon category is required.";
  } else if (!COUPON_CATEGORIES.includes(category)) {
    errors.category = `Category must be one of: ${COUPON_CATEGORIES.join(", ")}.`;
  }

  validateBundleDiscountCategoryField(values, errors);

  validateCouponRemarks(values, errors);

  if (
    values.verticalId === "" ||
    values.verticalId === null ||
    values.verticalId === undefined
  ) {
    errors.verticalId = "Vertical is required.";
  }

  validateCouponScopeCurrencyAndTenant(values, errors, {
    availableTenantOptionIds,
    selectedCurrencyIds,
    areTenantsConfirmed,
    isCreate: true,
  });

  const effectiveDate = normalizeDate(values.effectiveDate);
  const expiryDate = normalizeDate(values.expiryDate);

  if (effectiveDate === null) {
    errors.effectiveDate = "Effective date is required.";
  } else if (effectiveDate < today) {
    errors.effectiveDate = "Effective date must be today or a future date.";
  } else {
    const maxEffective = new Date(today);
    maxEffective.setDate(
      maxEffective.getDate() +
        COUPON_VALIDATION.MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS
    );
    if (effectiveDate > maxEffective) {
      errors.effectiveDate = `Effective date cannot be more than ${COUPON_VALIDATION.MAX_ALLOWED_EFFECTIVE_DATE_OFFSET_DAYS} days from today.`;
    }
  }

  if (expiryDate === null) {
    errors.expiryDate = "Expiry date is required.";
  } else if (effectiveDate !== null) {
    validateExpiryDateAgainstEffectiveDate(
      values.effectiveDate,
      values.expiryDate,
      errors
    );
  }

  validateDiscountTypeField(values, errors);
  validateDiscountValueField(values, errors);
  validateFlatMaxAllowedAmountMustBeEmpty(values, errors);
  validateCurrencySettingsFields(values, errors, { selectedCurrencyIds });
  validateTotalCouponCountField(values, errors);
  validateApplicableMultipleTimesField(values, errors);
  validateApplicableForSingleUserField(values, errors);
  validateUamIdField(values, errors);
  validatePackageApplicabilityFields(values, errors, { availablePackageIds });
  validatePlatformApplicabilityFields(values, errors);

  return {
    errors,
    isValid: Object.keys(errors).length === ZERO,
  };
};

export const validateEditCouponForm = (values, options = {}) => {
  const {
    availablePackageIds = [],
    availableTenantOptionIds = [],
    selectedCurrencyIds = [],
  } = options;
  const errors = {};

  validateExpiryDateAgainstEffectiveDate(
    values.effectiveDate,
    values.expiryDate,
    errors
  );

  validateCouponRemarks(values, errors);
  validateCouponScopeCurrencyAndTenant(values, errors, {
    availableTenantOptionIds,
    selectedCurrencyIds,
  });
  validateDiscountValueField(values, errors);
  validateFlatMaxAllowedAmountMustBeEmpty(values, errors);
  validateCurrencySettingsFields(values, errors, { selectedCurrencyIds });
  validateBundleDiscountCategoryField(values, errors, { isEdit: true });
  validateTotalCouponCountField(values, errors, {
    minimumUsedCount: values.usedCount ?? ZERO,
  });
  validateApplicableMultipleTimesField(values, errors);
  validatePackageApplicabilityFields(values, errors, { availablePackageIds });
  validatePlatformApplicabilityFields(values, errors);

  return {
    errors,
    isValid: Object.keys(errors).length === ZERO,
  };
};
